/**
 * AGENDA DE LA SAISON — une seule liste chronologique, construite depuis les sources
 * existantes. AUCUNE date n'est écrite ici :
 *   • rentrée et fermetures de l'école → calendrier officiel vaudois (vacancesScolaires.js),
 *     déjà décalé d'une semaine avant/après pour l'été comme le fait l'école ;
 *   • Portes Ouvertes → classeur de l'école (GET /api/reglages) ;
 *   • stages → lib/stages.js ; spectacles → pages/Spectacles.jsx ; audition → lib/auditions.js.
 */
import { VACANCES_SCOLAIRES, RENTREES } from "./vacancesScolaires";
import { SAISON } from "./dates";
import { STAGES } from "./stages";
import { AUDITION } from "./auditions";
import { representationsEnTexte } from "./representations";

const isoDe = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

export const jourLong = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return `${d.getDate()} ${MOIS[d.getMonth()]} ${d.getFullYear()}`;
};

/** « du 12 au 16 octobre 2026 » / « le 7 décembre 2026 » */
export const periodeLisible = (debut, fin) => {
  if (!fin || fin === debut) return `le ${jourLong(debut)}`;
  const a = new Date(`${debut}T00:00:00`);
  const b = new Date(`${fin}T00:00:00`);
  const memeMois = a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  if (memeMois) return `du ${a.getDate()} au ${b.getDate()} ${MOIS[b.getMonth()]} ${b.getFullYear()}`;
  return `du ${jourLong(debut)} au ${jourLong(fin)}`;
};

export const TYPES_AGENDA = {
  rentree: { label: "Rentrée", couleur: "#14B8C4" },
  fermeture: { label: "École fermée", couleur: "#DC2626" },
  portes_ouvertes: { label: "Portes Ouvertes", couleur: "#16A34A" },
  stage: { label: "Stage", couleur: "#F59E0B" },
  spectacle: { label: "Spectacle", couleur: "#9333EA" },
  audition: { label: "Audition", couleur: "#0E7A85" },
};

/**
 * @param {Array<{debut:string, fin:string}>} poRanges - Portes Ouvertes du classeur
 * @param {Array} spectacles - entrées de pages/Spectacles.jsx (avec date_iso)
 * @returns {Array} événements triés par date de début
 */
export function construireAgenda(poRanges = [], spectacles = []) {
  const evts = [];

  evts.push({
    id: "rentree",
    type: "rentree",
    debut: SAISON.rentree.iso,
    fin: null,
    titre: `Rentrée des cours — saison ${SAISON.libelle}`,
    detail: "Reprise de tous les cours selon le planning hebdomadaire, une semaine après la rentrée scolaire vaudoise.",
    lien: { to: "/planning", label: "Voir le planning" },
  });

  (poRanges || []).forEach((f, i) => {
    evts.push({
      id: `po-${f.debut}`,
      type: "portes_ouvertes",
      debut: f.debut,
      fin: f.fin,
      titre: "Portes Ouvertes — cours d'essai offerts",
      detail: "Tous les cours d'essai sont offerts pendant cette période, sans engagement. Réservation en ligne conseillée.",
      lien: { to: "/inscription?type=essai", label: "Réserver un essai" },
      cle: i,
    });
  });

  VACANCES_SCOLAIRES.forEach((v) => {
    evts.push({
      id: `fermeture-${isoDe(v.start)}`,
      type: "fermeture",
      debut: isoDe(v.start),
      fin: isoDe(v.end),
      titre: v.label.split(" — ")[0],
      detail: v.label.includes(" — ")
        ? v.label.split(" — ")[1].replace(/^l'école/, "L'école")
        : "Pas de cours pendant cette période : l'école suit le calendrier scolaire vaudois.",
    });
  });

  (STAGES || []).forEach((s) => {
    if (!s.date_debut) return;
    evts.push({
      id: `stage-${s.id}`,
      type: "stage",
      debut: s.date_debut,
      fin: s.date_fin || s.date_debut,
      titre: `${s.title} — ${s.theme}`,
      detail: `${s.age_range}${s.closed ? " · inscriptions fermées" : s.coming_soon ? " · programme et tarifs à venir" : ` · tarif dès ${s.tarif_from}`}`,
      lien: s.closed ? null : { to: `/stages/${s.id}`, label: "Voir le stage" },
    });
  });

  (spectacles || []).forEach((s) => {
    if (!s.date_iso) return;
    evts.push({
      id: `spectacle-${s.id}`,
      type: "spectacle",
      debut: s.date_iso,
      fin: s.date_fin_iso || s.date_iso,
      titre: s.title,
      detail: [s.subtitle, s.location, s.representations && representationsEnTexte(s.representations)].filter(Boolean).join(" · "),
      lien: s.reservation_url
        ? { to: s.reservation_url, label: "Réserver une place" }
        : { to: "/spectacles", label: "Voir les spectacles" },
    });
  });

  if (AUDITION.date_iso) {
    evts.push({
      id: `audition-${AUDITION.id}`,
      type: "audition",
      debut: AUDITION.date_iso,
      fin: null,
      titre: AUDITION.title,
      detail: `${AUDITION.groupes} · ${AUDITION.lieu}`,
      lien: { to: AUDITION.url, label: "S'inscrire à l'audition" },
    });
  }

  // Une seule saison à l'écran : du 1er juillet précédant la rentrée jusqu'à la veille de la
  // rentrée suivante. Sans cette borne, le calendrier vaudois (connu jusqu'en 2031) déverserait
  // cinq années de vacances dans la page.
  const debutSaison = `${Number(SAISON.rentree.iso.slice(0, 4))}-07-01`;
  const rentreeSuivante = RENTREES.find((iso) => iso > SAISON.rentree.iso) || "9999-12-31";
  return evts
    .filter((e) => (e.fin || e.debut) >= debutSaison && e.debut < rentreeSuivante)
    .sort((a, b) => a.debut.localeCompare(b.debut));
}

/** Événements dont la fin n'est pas encore passée. */
export function aVenir(evts, now = new Date()) {
  const j = isoDe(now);
  return evts.filter((e) => (e.fin || e.debut) >= j);
}
